/**
 * What the quartermaster's boards share: the stores' crates in each era's colours (their art), and
 * the goods told apart by the kit's six colours.
 */
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { PALETTE } from '../kit/tokens.js';

/** A crate in each era: cardboard on Earth, glowing plastic in the city, army green in the war, rough wood under the sand, a harvest box on the surface. */
export interface CratePalette {
  top: string;
  body: string;
  edge: string;
  metal: string;
  /** The stencilled mark: always near white, so a shape is read by its outline alone. */
  mark: string;
}

export const CRATE_PAL: Record<Era, CratePalette> = {
  stars: { top: '#d2a468', body: '#8d6232', edge: '#4f3417', metal: '#c3ccd3', mark: '#fff7e6' },
  neon: { top: '#40246a', body: '#1c0f34', edge: ERA_SKIN.neon.ink, metal: ERA_SKIN.neon.haze, mark: '#ffffff' },
  debris: { top: '#76805d', body: '#3c4230', edge: '#1f2318', metal: '#aab09f', mark: '#f5ecd4' },
  dust: { top: '#c29155', body: '#7a5129', edge: '#452c12', metal: '#e0b981', mark: '#fff1d8' },
  canopy: { top: '#b48d56', body: '#6b4e2c', edge: '#3d2b16', metal: '#c9d8b0', mark: '#fbffea' },
};

/** The goods packed in a crate, told apart: the kit's six colours, the same in every era. */
export const GOODS: readonly string[] = PALETTE;

/** Lighter or darker by `amount` (−1 to 1), for bevels and gradients. */
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1, 7), 16);
  const mix = (c: number) => Math.round(amount >= 0 ? c + (255 - c) * amount : c * (1 + amount));
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
